import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";
import { isRegionNotOffered, type AccountErasureRequest, type AccountErasureRequested, type EvidenceReference, type LearnerProfile, type ExperienceLevel, type PracticeAttemptRequest, type ProfileExtracted, type RehearsalReflection, type SettingsPatch, type WhoAmI } from "@pushback-and-chaos/contracts";
import { afterCoachDataRemoved, coachDataOffered } from "./data/coach-cache.js";
import type { PracticeAction } from "./screens/practice-panel.js";
import { ProblemJsonError } from "@pushback-and-chaos/client";
import { retryDelay, retryTransient } from "./data/retry.js";
import { apiClient, DEV_PANEL_ENABLED, IS_PREVIEW } from "./data/transport.js";
import {
  setStoredGuestId,
  setStoredProfileId,
  setStoredSessionId,
  storedGuestId,
  storedProfileId,
  storedSessionId,
} from "./data/identity.js";
import { useSession } from "./data/use-session.js";
import { useBudget, useDevTelemetry } from "./data/dev-telemetry.js";
import {
  authModeFromHash,
  bookArrivalFromHash,
  bookSessionFromHash,
  hashForArrivalBook,
  hashForAuth,
  hashForBook,
  hashForRoute,
  hashWithoutAccountToken,
  mailTokenFromHash,
  READING_ROUTES,
  REGION_OPEN_ROUTES,
  routeFromHash,
  type AuthMode,
  type RouteName,
} from "./routes.js";
import { CasebookScreen } from "./screens/casebook.js";
import { ArrivalBookScreen } from "./screens/arrival-book.js";
import { activeCaseSlug } from "./data/active-case.js";
import { Landing } from "./screens/landing.js";
import { Privacy } from "./screens/privacy.js";
import { CheckInPage } from "./screens/check-in.js";
import { UnsubscribePage } from "./screens/unsubscribe.js";
import { Auth, type AuthDraft, type EmailSignInResult } from "./screens/auth.js";
import { Terms } from "./screens/terms.js";
import { VerifyEmailPage } from "./screens/verify-email.js";
import { ResetPasswordPage } from "./screens/reset-password.js";
import { TermsPaper } from "./ui/terms-paper.js";
import { isEmailNotVerified, signInRefusalWords } from "./data/auth-errors.js";
import { AppShell } from "./screens/shell.js";
import { BeatPresentation, useBeatPager } from "./screens/beat.js";
import { PlaySurface } from "./screens/play.js";
import { SettingsSheet } from "./screens/settings.js";
import { AuditView, CrossExam, Debrief, EngagementOver, RecommendationComposer, type AuditPayload, type DebriefResponse } from "./screens/endgame.js";
import { ScreenErrorBoundary } from "./ui/error-boundary.js";
import { PracticeRecordScreen } from "./screens/record.js";
import { CaseLibrary, type LibraryCase } from "./screens/library.js";
import { HomeScreen, type OnboardingState } from "./screens/home.js";
import { useEmailChoices } from "./data/email-choices.js";
import { useVisitCounting } from "./data/visit-count.js";
import { acrossThePlace, applyPlace } from "./design/place.js";
import { installPressRuns } from "./design/press.js";
import { warmThreshold } from "./design/threshold.js";
import { accountExportDoorOpen, downloadAccountExport } from "./data/account-export.js";
import { erasureDoorOpen, pendingErasure, settleErasureRequest, type ErasureConfirmResult, type ErasureRequestedFacts } from "./data/account-erasure.js";
import { identityQuery } from "./data/query-client.js";
import { ErasureGraceNotice, ErasureRequestedNotice } from "./ui/account-erasure.js";
import { RegionNotOffered } from "./ui/region-not-offered.js";
import { useRegionRefusals } from "./data/region-refusal.js";
import { SkillsScreen } from "./screens/skills.js";
import { CASE_TENSION, COACH_DATA, PLAY, PRACTICE, PRIVACY } from "./design/copy.js";
import { DevPanel } from "./dev/panel.js";
import { DocumentGallery } from "./dev/gallery.js";
import { NarrativeStage } from "./dev/stage.js";
import { PlayReviewBench } from "./dev/play-review.js";
import { FrontDoorReviewBench } from "./dev/front-door-review.js";
import { PreviewNote } from "./ui/preview-note.js";

/**
 * THE LIBRARY QUERY: every published, registered case, with the industry and
 * engagement question its card shows. One request (2026-09-24): it was one
 * more per case for the opening, twenty-odd on every page load, and that burst
 * spent the API's rate limit before the desk's own requests went out.
 */
async function loadLibrary(client: ReturnType<typeof apiClient>): Promise<readonly LibraryCase[]> {
  const listed = await client.listCases();
  // The shelf reads left to right in the planned case order (the order the
  // tension lines are kept in), so the first case is the first card; a case
  // without a line yet sits after them in the server's order.
  const planned = Object.keys(CASE_TENSION);
  const rank = (slug: string): number => {
    const i = planned.indexOf(slug);
    return i === -1 ? planned.length : i;
  };
  return [...listed]
    .sort((a, b) => rank(a.slug) - rank(b.slug))
    .map((item) => ({
      slug: item.slug,
      track: item.track,
      client: item.client,
      primaryDimension: item.primaryDimension,
      pressureContext: item.pressureContext,
      industry: item.industry,
      engagementQuestion: item.engagementQuestion,
      tension: CASE_TENSION[item.slug] ?? item.engagementQuestion,
    }));
}

/**
 * THE PRACTICE RECORD'S QUERY, as options (2026-10-11): read by the desk's
 * `useQuery`, and fetched again by name after the coach's data is removed, so
 * the one key `["practiceRecord", guestId]` is written in one place.
 */
function practiceRecordQuery(client: ReturnType<typeof apiClient>, guestId: string | null) {
  return queryOptions({
    queryKey: ["practiceRecord", guestId],
    queryFn: () => client.getPracticeRecord(),
    retry: retryTransient,
    retryDelay,
  });
}

/**
 * THE ROOT (docs/ACCOUNT-ERASURE-DESIGN-2026-10-20.md, "Web"): who this browser
 * is decides between the app and the grace notice. A signed-in account whose
 * deletion is pending is refused by every route but its identity and its
 * erasure doors, so it sees the notice, the whole screen, and never the app
 * behind it. The app renders while the identity is still being read, as it
 * always has; a refusal on the way asks again (`createAppQueryClient`).
 *
 * "keep my account." cancels the request and asks who this is again, so the
 * app opens as before. "log out." ends the account's sign-in (better-auth's
 * session) and nothing else: the app's own cookie stays, because a guest
 * played on this browser since the request is reachable only through it (no
 * guest work is claimed into a pending account, so it is still that guest's).
 * Then it asks who this is, and the browser lands as whoever the cookie now
 * names: that guest, its cache keys kept, or nobody, on the landing page.
 * Cached reads, and cache keys that were not that guest's, are forgotten.
 */
export function AppRoot(): ReactNode {
  const client = apiClient();
  const queryClient = useQueryClient();
  const identity = useQuery(identityQuery(client));
  const eraseAfter = pendingErasure(identity.data);

  const keepAccount = useCallback(async (): Promise<boolean> => {
    try {
      await client.cancelErasure();
    } catch {
      return false;
    }
    // Every read made while the account was refused goes, so the app opens on fresh answers.
    queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== "identity" });
    await queryClient.invalidateQueries({ queryKey: ["identity"] });
    return true;
  }, [client, queryClient]);

  const logOut = useCallback(async (): Promise<boolean> => {
    try {
      await client.signOut();
    } catch {
      return false;
    }
    // Read outside the cache, so the cache keys are settled before the app mounts on the answer.
    // If nobody answers, the account is out all the same: nobody, until the next read says more.
    const next = await client.getIdentity().catch((): WhoAmI => ({ kind: "none", learnerId: null, erasure: null, termsCurrent: true }));
    if (next.learnerId === null || next.learnerId !== storedGuestId()) {
      setStoredGuestId(null);
      setStoredSessionId(null);
      setStoredProfileId(null);
    }
    queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== "identity" });
    globalThis.location.hash = hashForRoute("landing");
    queryClient.setQueryData<WhoAmI>(["identity"], next);
    return true;
  }, [client, queryClient]);

  if (eraseAfter === null) return <App />;
  return <ErasureGraceNotice eraseAfter={eraseAfter} onKeep={keepAccount} onLogOut={logOut} />;
}

/**
 * THE LEARNER'S APP, and the only one this bundle holds (ADR 0024). Nothing
 * of the operator's workspace is built into it: no screen, no copy, no call.
 * An old operator hash is an unknown hash like any other: the landing page,
 * the hash left as typed, and a signed-in learner sent home as usual
 * (no-operator-door.test.ts holds this).
 */
export function App(): ReactNode {
  const client = apiClient();
  const queryClient = useQueryClient();

  const [route, setRoute] = useState<RouteName>(() => routeFromHash(globalThis.location.hash));
  /**
   * THE REGION GATE (Andre's rulings of 2026-10-27 and answers of 2026-10-28):
   * the api refused a door that writes or calls a model from the EU, the EEA or
   * the UK with 451 REGION_NOT_OFFERED, whichever screen asked
   * (data/region-refusal.ts), or sent this browser to `#/not-offered` from
   * Google's callback. The app gives way to its one screen until the page is
   * reloaded, and tries the refused door no more; the doors that stay open are
   * on it.
   */
  const regionRefusals = useRegionRefusals();
  /**
   * Whether the learner has followed one of the screen's doors (a reading page,
   * signing in) since the last refusal: the screen then gives way while they
   * are on a page those doors lead to (REGION_OPEN_ROUTES), and comes back on
   * any other page, or with the next refusal.
   */
  const [regionDoorFollowed, setRegionDoorFollowed] = useState(false);
  useEffect(() => {
    setRegionDoorFollowed(false);
  }, [regionRefusals]);
  const regionRefused = route === "not-offered" || (regionRefusals > 0 && !(regionDoorFollowed && REGION_OPEN_ROUTES.has(route)));
  /** The auth screen's tab, from `#/auth?mode=…` (a guest's "create an account." opens on signing up). */
  const [authMode, setAuthMode] = useState<AuthMode>(() => authModeFromHash(globalThis.location.hash));
  /** The finished engagement a casebook shows (2026-09-24), from `#/book?session=<id>` so a reload keeps it. */
  const [bookSessionId, setBookSessionId] = useState<string | null>(() => bookSessionFromHash(globalThis.location.hash));
  /**
   * The signed token a mail link carries (CME stage 3b, 2026-10-11), from `#/unsubscribe?token=` or
   * `#/check-in?token=`, and since 2026-10-22 the account emails' one-time tokens, from
   * `#/verify?token=` and `#/reset?token=`; opaque here, verified by the server.
   */
  const [mailToken, setMailToken] = useState<string | null>(() => mailTokenFromHash(globalThis.location.hash));
  /** Whether `#/book?arrival=1` names the arrival book (2026-10-11), the record's first book, rather than a casebook. */
  const [bookArrival, setBookArrival] = useState<boolean>(() => bookArrivalFromHash(globalThis.location.hash));
  const [guestId, setGuestId] = useState<string | null>(storedGuestId);
  const [sessionId, setSessionId] = useState<string | null>(storedSessionId);
  const [profile, setProfile] = useState<LearnerProfile | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [declined, setDeclined] = useState<string | null>(null);
  const [telemetryToken, setTelemetryToken] = useState(0);
  /** The learner's line to the coach while its reply is being written (the front door shows it in place). */
  const [coachPending, setCoachPending] = useState<string | null>(null);
  /** The engine turn the debrief should open at, set by "view the moment" (C05); null otherwise. */
  const [focusTurn, setFocusTurn] = useState<number | null>(null);
  /**
   * THE INTAKE (onboarding pass, 2026-09-13). The server's last answer: the
   * draft so far, the one thing still missing, and the complete profile once
   * there is one (Law 6: inferred, then confirmed). `exchanges` is the intake
   * as it was said, for the thread; the server holds the transcript of record.
   */
  const [intake, setIntake] = useState<ProfileExtracted | null>(null);
  const [exchanges, setExchanges] = useState<readonly { readonly from: "coach" | "you"; readonly text: string }[]>([]);
  /** The rehearsal's reflection once the one move has been made. */
  const [rehearsal, setRehearsal] = useState<RehearsalReflection | null>(null);

  const beat = useBeatPager();
  const budget = useBudget();
  // First-party visit counting (ADR 0022): one beacon per settled route, never under GPC, DNT or the opt-out.
  useVisitCounting(route);
  // THE PLACE (2026-11-02): the document root says whether this is the study
  // or the book, from the route alone, and the stylesheet draws from that
  // (design/place.ts; routes.ts `placeOf`).
  useEffect(() => {
    applyPlace(route);
  }, [route]);
  // THE STUDY'S PRESS RUNS AND THE THRESHOLD'S GRAIN (2026-11-04): one
  // document-level listener marks a press on a study button for the
  // stylesheet's fray (design/press.ts), and the crossing's grain field is
  // built on idle time so the first crossing never waits for it.
  useEffect(() => {
    const coolThreshold = warmThreshold();
    const removePressRuns = installPressRuns();
    return () => { coolThreshold(); removePressRuns(); };
  }, []);
  /** The route as it stands, for the hash sync, which decides whether a change crosses the threshold between the places. */
  const routeRef = useRef(route);
  routeRef.current = route;

  /**
   * Where "back." on the terms and the privacy page goes (2026-10-22; the
   * review made it the exact address): the whole hash, query included
   * (`#/auth?mode=sign-up`), of the last page shown before the reading
   * began, so moving between the two pages never makes either one's "back."
   * the other. Kept whenever the address shows any other page; null when a
   * reading page was the first thing opened (a cold link, or settings' new
   * tab).
   */
  const returnHash = useRef<string | null>(READING_ROUTES.has(route) ? null : globalThis.location.hash);
  const keepReturnHash = useCallback((): void => {
    if (!READING_ROUTES.has(routeFromHash(globalThis.location.hash))) returnHash.current = globalThis.location.hash;
  }, []);
  // A route changed without a hashchange (the landing page's replace).
  useEffect(keepReturnHash, [route, keepReturnHash]);

  // The hash is the whole router; keep the two in sync in both directions so
  // a reload and the browser Back button both land somewhere sensible.
  // ONE OWNER OF A CROSSING (2026-11-04): this sync, on `hashchange`, is the
  // only place a navigation is applied. The helpers below set the hash and
  // nothing else, so one move starts one transition (design/place.ts).
  const syncFromHash = useCallback((): void => {
    keepReturnHash();
    const hash = globalThis.location.hash;
    const next = routeFromHash(hash);
    // Across the threshold between the places (2026-11-02), the change is applied inside a cross-fade; within one place, at once (design/place.ts).
    acrossThePlace(routeRef.current, next, () => {
      setRoute(next);
      setAuthMode(authModeFromHash(hash));
      setBookSessionId(bookSessionFromHash(hash));
      setMailToken(mailTokenFromHash(hash));
      setBookArrival(bookArrivalFromHash(hash));
    });
  }, [keepReturnHash]);
  useEffect(() => {
    globalThis.addEventListener("hashchange", syncFromHash);
    return () => globalThis.removeEventListener("hashchange", syncFromHash);
  }, [syncFromHash]);

  /**
   * An account email's link (`#/verify?token=`, `#/reset?token=`) leaves the address once the
   * server has answered in a way that spends or settles its token (the pre-release review, round
   * two, 2026-10-22): the page reports it (confirmed, confirmed before or spent; saved or spent),
   * and its entry is replaced by the bare route, so the tab's history no longer offers it to
   * whoever presses Back on a shared browser. Until then the link stays as it came: "Open in
   * Safari" from a mail app's own browser, a copied address, a reload and this app mounted again
   * all read the token from the address, and a request not answered, or a limit, leaves it there
   * because the link may still work. Replaced, not pushed: it fires no hashchange, the page keeps
   * the token it holds, and "back." from a reading page is kept to the bare address too.
   */
  const settleAccountLink = useCallback(
    (token: string): void => {
      const bare = hashWithoutAccountToken(globalThis.location.hash, token);
      if (bare === null) return;
      globalThis.history.replaceState(null, "", bare);
      keepReturnHash();
    },
    [keepReturnHash],
  );

  /**
   * Every helper here moves by setting the hash alone (2026-11-04): the
   * `hashchange` it provokes reaches syncFromHash, which derives the route
   * and the rest from the address and applies them inside the one
   * transition. A target that equals the current hash fires no event and
   * changes nothing; the state a helper used to set itself lands one task
   * later, carried by the hash.
   */
  const navigate = useCallback((next: RouteName) => {
    globalThis.location.hash = hashForRoute(next);
  }, []);

  /**
   * What the sign-in card held when the learner followed its terms or
   * privacy notice (the review of 2026-10-22): what was typed and the
   * check-your-email state, handed back when "back." returns to the card.
   * In memory only, and only while the learner is on the card or one of the
   * two pages: anywhere else forgets it.
   */
  const authDraft = useRef<AuthDraft | undefined>(undefined);
  useEffect(() => {
    if (route !== "auth" && !READING_ROUTES.has(route)) authDraft.current = undefined;
  }, [route]);
  /** "not now." on the terms paper (2026-10-22): put down until the app is next opened. */
  const [termsPaperDown, setTermsPaperDown] = useState(false);
  /** Opens a finished engagement's casebook, from the library's Completed box or a record row. */
  const openBook = useCallback((id: string) => {
    globalThis.location.hash = hashForBook(id);
  }, []);
  /** Opens the arrival book (2026-10-11) from the record's first row. */
  const openArrivalBook = useCallback(() => {
    globalThis.location.hash = hashForArrivalBook();
  }, []);

  /** The auth screen on a chosen tab: the guest paper's two doors (2026-09-24). */
  const openAuth = useCallback((mode: AuthMode) => {
    globalThis.location.hash = hashForAuth(mode);
  }, []);

  /**
   * WHO THIS BROWSER IS, from the server (2026-09-23). The stored guest id is
   * only a cache key and exists only for someone who pressed "Guest Session."
   * on this device; an account signed in with email or Google never wrote
   * one. The cookies are the truth, so the landing page asks.
   */
  const identity = useQuery(identityQuery(client));
  const signedIn = identity.data !== undefined && identity.data.kind !== "none";
  /** "back." on a reading page: the address the reading began from; reached cold, home when signed in, else sign in. */
  const readingReturn: string = returnHash.current ?? hashForRoute(signedIn ? "home" : "auth");
  const backFromReading = useCallback(() => {
    globalThis.location.hash = readingReturn;
  }, [readingReturn]);

  // A signed-in learner has no use for the landing page: send them home. The
  // landing entry is REPLACED, not pushed over, so Back leaves the site rather
  // than bouncing off the landing page into Home again.
  useEffect(() => {
    if (route !== "landing" || !signedIn) return;
    globalThis.history.replaceState(null, "", hashForRoute("home"));
    setRoute("home");
  }, [route, signedIn]);

  // An account signed in on a device that never held a guest has no cache key,
  // and every identity-scoped query waits on one: adopt the server's.
  const learnerId = identity.data?.learnerId ?? null;
  useEffect(() => {
    if (learnerId === null || guestId !== null) return;
    setStoredGuestId(learnerId);
    setGuestId(learnerId);
  }, [learnerId, guestId]);

  // Nothing to re-attach on mount any more: identity is a signed httpOnly cookie
  // the browser resends on its own, so a reload is authenticated without the app
  // doing anything. The stored guest id below is only a cache key for which
  // session the UI was last showing.

  const session = useSession(sessionId, () => setTelemetryToken((n) => n + 1));
  const telemetry = useDevTelemetry(sessionId, telemetryToken);

  const library = useQuery({
    queryKey: ["library"],
    queryFn: () => loadLibrary(client),
    staleTime: Number.POSITIVE_INFINITY,
    retry: retryTransient,
    retryDelay,
  });

  // Which case the learner is looking at outside a session: the one they
  // chose to open or to read the prologue of. Inside a session, the session's
  // own case wins — a reload must never show another case's opening.
  const [chosenSlug, setChosenSlug] = useState<string | null>(null);
  const activeSlug = activeCaseSlug({ route, chosenSlug, sessionSlug: session.data?.caseSlug ?? null, firstSlug: library.data?.[0]?.slug ?? null });
  const opening = useQuery({
    queryKey: ["opening", activeSlug],
    enabled: activeSlug !== null,
    queryFn: () => client.getCaseOpening(activeSlug as string),
    staleTime: Number.POSITIVE_INFINITY,
  });

  const settings = useQuery({
    queryKey: ["settings", guestId],
    enabled: guestId !== null,
    queryFn: () => client.getSettings(),
  });

  /**
   * Which halves of the voice this deployment has wired (ADR 0011). Fixed for
   * the life of the process, so fetched once; a deployment with neither half
   * simply shows the microphone and the listen controls as "not yet."
   */
  const voiceCapabilities = useQuery({
    queryKey: ["voice", guestId],
    enabled: guestId !== null,
    queryFn: () => client.getVoiceCapabilities(),
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });

  /**
   * THE SHELF STATE (library pass, 2026-09-08): the saved box, the plan
   * acknowledgement, the unlock and the open engagements — one request,
   * assembled server-side. Read on the library and on the record (the
   * acknowledgement lives on the record's own surface).
   */
  const libraryRoutes: readonly RouteName[] = ["home", "library", "profile", "coach"];
  const libraryState = useQuery({
    queryKey: ["libraryState", guestId],
    enabled: guestId !== null && libraryRoutes.includes(route),
    queryFn: () => client.getLibrary(),
    retry: retryTransient,
    retryDelay,
  });

  const practiceRecord = useQuery({
    ...practiceRecordQuery(client, guestId),
    enabled: guestId !== null && libraryRoutes.includes(route),
  });
  // WHETHER THIS LEARNER HAS A CONFIRMED PROFILE is the server's to say (2026-09-23). The
  // stored profile id is written only by onboarding on this device; a learner who onboarded
  // before it existed, on another device, or after clearing the browser has a profile the
  // coach speaks from and no id here, and on production that hid the practice panel, the
  // focus and the settings controls from exactly such a learner. The record's profile is
  // the source; the stored id remains a shortcut for the moment before the record loads.
  const hasProfile = storedProfileId() !== null || (practiceRecord.data?.profile ?? null) !== null;
  // THE COACH'S-DATA CONTROLS sit on a CONFIRMED profile (2026-10-11): the record's word
  // alone. The stored id exists from the intake's first draft, and on it "remove it all."
  // was reachable during the read-back, where it would remove the intake being read back.
  const coachDataControls = coachDataOffered(practiceRecord.data);

  /**
   * THE FRONT DOOR (home pass, 2026-09-09): the coach's opening and the kept
   * conversation. The opening is cached server-side against the record it
   * was written over, so asking again is cheap; the thread is what was said.
   */
  const coachOpening = useQuery({
    queryKey: ["coachOpening", guestId],
    // The record page reads the same opening as its summary (2026-09-22):
    // the coach's reading of the whole record, cached server-side against it.
    enabled: guestId !== null && (route === "home" || route === "profile" || route === "coach"),
    queryFn: () => client.getCoachOpening(),
    retry: retryTransient,
    retryDelay,
    staleTime: 60_000,
  });

  const coachThread = useQuery({
    queryKey: ["coachThread", guestId],
    enabled: guestId !== null && (route === "home" || route === "profile" || route === "coach"),
    queryFn: () => client.getCoachThread(),
    retry: retryTransient,
    retryDelay,
  });

  const skills = useQuery({
    queryKey: ["skills"],
    enabled: route === "skills",
    queryFn: () => client.getSkills(),
    staleTime: Number.POSITIVE_INFINITY,
  });

  const debrief = useQuery({
    queryKey: ["debrief", sessionId],
    enabled: sessionId !== null && (route === "debrief" || route === "audit"),
    queryFn: () => client.getDebrief(sessionId as string) as Promise<DebriefResponse>,
    retry: false,
    // The debrief is written by the worker after the fourth cross-exam
    // answer; the route 409s until it exists. Keep asking until it does.
    refetchInterval: (query) => (query.state.data === undefined ? 4000 : false),
  });

  /**
   * Whether the address is verified, for "delete my account."'s dialog: emailed copies then,
   * downloads otherwise (ruling 17). Read only while an account has the settings sheet open.
   */
  const erasureStatus = useQuery({
    queryKey: ["erasure"],
    enabled: settingsOpen && erasureDoorOpen(identity.data?.kind),
    queryFn: () => client.getErasure(),
    retry: retryTransient,
    retryDelay,
  });

  const audit = useQuery({
    queryKey: ["audit", sessionId],
    enabled: sessionId !== null && (route === "debrief" || route === "audit"),
    queryFn: () => client.getAudit(sessionId as string) as Promise<AuditPayload>,
    retry: false,
  });

  const guardedCall = useCallback(async <T,>(fn: () => Promise<T>): Promise<T | null> => {
    setBusy(true);
    setError(null);
    try {
      return await fn();
    } catch (caught) {
      // The region gate's refusal is the screen's to say (data/region-refusal.ts heard it from the client), never an error line.
      if (caught instanceof ProblemJsonError && isRegionNotOffered(caught)) return null;
      setError(
        caught instanceof ProblemJsonError
          ? caught.message
          : "Could not reach the engagement. The server may be down — check that `pnpm dev` is still running.",
      );
      return null;
    } finally {
      setBusy(false);
    }
  }, []);

  const setSavedCases = useCallback(
    async (slugs: readonly string[]) => {
      const next = await guardedCall(() => client.setSavedCases(slugs));
      if (next !== null) queryClient.setQueryData(["libraryState", guestId], next);
    },
    [client, guardedCall, queryClient, guestId],
  );

  const acknowledgePlan = useCallback(async () => {
    const next = await guardedCall(() => client.acknowledgePlan());
    if (next !== null) queryClient.setQueryData(["libraryState", guestId], next);
  }, [client, guardedCall, queryClient, guestId]);

  /**
   * Walk back into an engagement the library lists as open — any of them, not
   * only the last one this browser showed. Straight to the book (2026-09-22):
   * the title page and the prologue are its first pages now, always there to
   * turn back to, so a returning learner lands on the story's newest page
   * with the framing one turn behind them.
   */
  const continueSession = useCallback(
    (id: string, slug?: string) => {
      setDeclined(null);
      setStoredSessionId(id);
      setSessionId(id);
      if (slug !== undefined) setChosenSlug(slug);
      navigate("play");
    },
    [navigate],
  );
  /**
   * "VIEW THE MOMENT" (C05): a reference the server built from what the coach
   * cited names a finished session the learner owns and the turn its debrief
   * tape lists. Open that debrief there. The debrief route's own ownership
   * check stands between the press and the page, exactly as from the library.
   */
  const viewMoment = useCallback(
    (reference: EvidenceReference) => {
      setStoredSessionId(reference.destination.sessionId);
      setSessionId(reference.destination.sessionId);
      setFocusTurn(reference.destination.turn);
      navigate("debrief");
    },
    [navigate],
  );

  /** THE PRACTICE EXERCISE (Release C): the server's projection, re-read after every control. */
  const practice = useQuery({
    queryKey: ["practice", guestId],
    enabled: guestId !== null && hasProfile && (route === "home" || route === "profile" || route === "coach"),
    queryFn: () => client.getPractice(),
    retry: retryTransient,
    retryDelay,
  });
  const [practiceBusy, setPracticeBusy] = useState(false);
  /**
   * THE DESK'S OWN ERROR LINE (2026-09-23): a refusal from the focus or
   * practice routes is said on the paper the learner is holding, in the
   * server's words, never in the coach's column as "that did not reach your
   * coach". Cleared by the next thing the learner does on the desk.
   */
  const [deskError, setDeskError] = useState<string | null>(null);
  const deskCall = useCallback(
    async (work: () => Promise<unknown>, key: "practice" | "focus") => {
      setPracticeBusy(true);
      setDeskError(null);
      try {
        await work();
      } catch (caught) {
        setDeskError(caught instanceof ProblemJsonError ? caught.message : PRACTICE.unreachable);
      } finally {
        await queryClient.invalidateQueries({ queryKey: [key] });
        setPracticeBusy(false);
      }
    },
    [queryClient],
  );
  const practiceCall = useCallback((work: () => Promise<unknown>) => deskCall(work, "practice"), [deskCall]);
  // THE FOCUS (Release D, C08): one thing the learner is working on, beside
  // the exercise; read from the server, changed by one checked event at a time.
  const focus = useQuery({
    queryKey: ["focus", guestId],
    enabled: guestId !== null && hasProfile && (route === "home" || route === "profile" || route === "coach"),
    queryFn: () => client.getFocus(),
    retry: retryTransient,
    retryDelay,
  });
  const focusCall = useCallback((work: () => Promise<unknown>) => deskCall(work, "focus"), [deskCall]);
  // EMAIL CHOICES (2026-10-07): nothing until the first Harborlight report, which the server
  // decides; from then on the card is on the desk and the section is in settings, for good.
  // One saved set and one save for both (data/email-choices.ts).
  const { emailChoices, emailBusy, saveEmailChoices } = useEmailChoices(client, guestId, coachOpening.data?.generatedAt ?? null);
  // THE DESK'S OTHER PAPERS (2026-09-23): the focus and the exercise go to
  // Home as data and doors; Home draws them as cards on the desk.
  const deskPapers = {
    focus: focus.data ?? null,
    practice: practice.data ?? null,
    // The desk forgets a card's place only when the hand and both papers have all
    // answered; a failed or pending one means "not here yet", never "gone".
    deskSettled: coachOpening.isSuccess && focus.isSuccess && practice.isSuccess && !coachOpening.isFetching && !focus.isFetching && !practice.isFetching,
    deskBusy: practiceBusy,
    deskError,
    onFocusSuggest: (text?: string) => void focusCall(async () => {
      // A named focus is the learner's own words: suggested and chosen in one breath.
      const suggested = await client.suggestFocus(text === undefined ? {} : { text });
      if (text !== undefined) await client.chooseFocus(suggested.focusId, text);
    }),
    onFocusChoose: (focusId: string) => void focusCall(() => client.chooseFocus(focusId)),
    onFocusAction: (focusId: string, action: "decline" | "done") => void focusCall(() => client.focusAction(focusId, action)),
    onFocusSelfReport: (focusId: string, text: string, context: "WORK" | "ENGAGEMENT" | "ELSEWHERE") => void focusCall(() => client.focusSelfReport(focusId, text, context)),
    onPracticeOffer: (briefId?: string) => void practiceCall(() => client.offerPractice(briefId === undefined ? {} : { briefId })),
    onPracticeAction: (exerciseId: string, action: PracticeAction) => void practiceCall(() => client.practiceAction(exerciseId, action)),
    onPracticeAttempt: (exerciseId: string, text: string, revises: number | null) => {
      const body: PracticeAttemptRequest = { text, idempotencyKey: `${exerciseId}:${String(Date.now())}`, ...(revises === null ? {} : { revises }) };
      void practiceCall(() => client.practiceAttempt(exerciseId, body));
    },
    emailChoices,
    emailBusy,
    emailSeasonSet: (settings.data?.targetSeason ?? null) !== null,
    onSaveEmailChoices: saveEmailChoices,
    onSignIn: () => openAuth("sign-in"),
  };
  // LAW 5: a copy of the account, guest work included, as one file (2026-10-12).
  const exportAccount = useCallback(() => downloadAccountExport(() => guardedCall(() => client.getAccountExport())), [client, guardedCall]);
  // LAW 5: a copy of what the coach keeps, and its removal whole. After a
  // removal every coach surface re-reads, so the page shows the coach
  // starting again rather than this page's memory of it.
  const exportCoachData = useCallback(async () => {
    const data = await guardedCall(() => client.exportCoachData());
    if (data === null) return;
    saveCoachData(data);
  }, [client, guardedCall]);
  /**
   * The erasure dialog's two downloads: the same copies as the doors above, but a failure is the
   * dialog's to say, over the sheet and over the app's notice behind it, so they answer whether
   * the copy was saved and leave the app's error alone. Busy holds the sheet as for any download.
   */
  const downloadBeforeErasure = useCallback(async (save: () => Promise<boolean>): Promise<boolean> => {
    setBusy(true);
    try {
      return await save();
    } catch {
      return false;
    } finally {
      setBusy(false);
    }
  }, []);
  const downloadCoachingBeforeErasure = useCallback(
    () =>
      downloadBeforeErasure(async () => {
        saveCoachData(await client.exportCoachData());
        return true;
      }),
    [client, downloadBeforeErasure],
  );
  const downloadAccountBeforeErasure = useCallback(
    () => downloadBeforeErasure(() => downloadAccountExport(() => client.getAccountExport())),
    [client, downloadBeforeErasure],
  );
  const deleteCoachData = useCallback(async () => {
    const removed = await guardedCall(() => client.deleteCoachData());
    if (removed === null) return;
    // The intake went with it (2026-10-11): nothing of it stays on this device
    // either, and the arrival book assembled from it is removed from the
    // cache, not refetched (`data/coach-cache.ts`).
    setIntake(null);
    setExchanges([]);
    setRehearsal(null);
    await afterCoachDataRemoved(queryClient);
    // The record says whether a profile remains. A stored id for one that
    // does not would keep the desk asking for papers nobody has.
    const record = await queryClient.fetchQuery({ ...practiceRecordQuery(client, guestId), staleTime: 0 }).catch(() => null);
    if (record !== null && record.profile === null) setStoredProfileId(null);
  }, [client, guardedCall, queryClient, guestId]);
  const [reflectionBusy, setReflectionBusy] = useState(false);
  const submitReflection = useCallback(
    async (answer: string) => {
      setReflectionBusy(true);
      try {
        await client.submitReflection(answer);
        await queryClient.invalidateQueries({ queryKey: ["practiceRecord"] });
      } catch {
        /* the record refetch shows the honest state either way */
      } finally {
        setReflectionBusy(false);
      }
    },
    [queryClient],
  );

  /**
   * "Guest Session." continues as the guest this browser already holds; the
   * server keeps that identity unless asked for a fresh one. Pressing it used
   * to mint a second guest every time and orphan the first one's work.
   */
  /** Everything this browser cached for an identity it no longer holds. */
  const forgetIdentity = useCallback(() => {
    setStoredSessionId(null);
    setSessionId(null);
    setStoredProfileId(null);
    setProfile(null);
    setIntake(null);
    setExchanges([]);
    setRehearsal(null);
    // Everything but who this browser is: the caller says that next (nobody,
    // or asks again), and the root's own reading of it must not be orphaned.
    queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== "identity" });
  }, [queryClient]);

  const startGuest = useCallback(
    async (fresh = false) => {
      // A FRESH guest means a fresh identity, whatever this browser held. A
      // signed-in account outranks any guest cookie in the server's identity
      // resolution, so "start a fresh guest." on a signed-in browser used to
      // mint a guest nobody would ever be (found on production, 2026-09-13):
      // sign out first, and forget everything cached for the old identity.
      if (fresh) {
        try {
          await client.signOut();
        } catch {
          /* not signed in: nothing to end */
        }
      }
      const created = await guardedCall(() => client.createGuest({ fresh }));
      if (created === null) return;
      if (fresh || created.guestId !== storedGuestId()) forgetIdentity();
      setStoredGuestId(created.guestId);
      setGuestId(created.guestId);
      await queryClient.invalidateQueries({ queryKey: ["identity"] });
      // The front door is where a new learner meets the coach; the separate
      // onboarding screen is kept only for old links.
      navigate("home");
    },
    [client, guardedCall, navigate, queryClient, forgetIdentity],
  );

  /**
   * "Log out." (2026-09-23): ends the account's session and this browser's own
   * cookie, forgets what was cached for it, and returns to the landing page —
   * which, with nobody signed in, now stays put.
   */
  const logOut = useCallback(async () => {
    const done = await guardedCall(() => client.logOut());
    if (done === null) return;
    setStoredGuestId(null);
    setGuestId(null);
    forgetIdentity();
    // The server has just said so; asking again would only race the redirect.
    queryClient.setQueryData<WhoAmI>(["identity"], { kind: "none", learnerId: null, erasure: null, termsCurrent: true });
    navigate("landing");
  }, [client, guardedCall, forgetIdentity, navigate, queryClient]);

  /**
   * "delete my account." confirmed (docs/ACCOUNT-ERASURE-DESIGN-2026-10-20.md). The request is
   * made outside `guardedCall`: its failure belongs to the dialog, which stays and says so, and
   * must not surface as the app's notice behind it. Once it is made the server has signed the
   * account out everywhere, so this browser runs its own log out, and the learner lands on the
   * notice with the moment over the landing page. When the answer did not come, who this is now
   * settles whether it was made (`settleErasureRequest`), so a request that committed and lost
   * its answer is not reported as nothing having changed.
   */
  const [erasureRequested, setErasureRequested] = useState<ErasureRequestedFacts | null>(null);
  const account = identity.data?.kind === "user" ? identity.data.learnerId : null;
  const requestErasure = useCallback(
    async (choices: AccountErasureRequest): Promise<ErasureConfirmResult> => {
      let facts: ErasureRequestedFacts;
      try {
        const requested: AccountErasureRequested = await client.requestErasure(choices);
        facts = { eraseAfter: requested.eraseAfter, email: requested };
      } catch (caught) {
        const outcome = await settleErasureRequest(caught, () => client.getIdentity(), account);
        if (outcome.kind !== "requested") return outcome.kind;
        facts = outcome.facts;
      }
      setSettingsOpen(false);
      await logOut();
      setErasureRequested(facts);
      return "requested";
    },
    [client, logOut, account],
  );

  /**
   * Signing in or up. The session is a cookie the server sets, so there is
   * nothing to store here — work done as a guest follows the learner onto the
   * new account on the next request, server-side.
   *
   * HARD VERIFICATION (ruling 8, 2026-10-22): an email sign-up makes no
   * session (better-auth answers `token: null`), and a sign-in with the right
   * password for an unconfirmed address is refused with 403
   * EMAIL_NOT_VERIFIED. Both answer `verify`, and the card asks the learner to
   * confirm the address; nothing is asked of the server and home is not
   * opened. Any other refusal is said in the card's words, by code
   * (data/auth-errors.ts), never better-auth's English.
   */
  const signInWithEmail = useCallback(
    async (input: { email: string; password: string; name: string; mode: "sign-in" | "sign-up" }): Promise<EmailSignInResult> => {
      setBusy(true);
      setError(null);
      try {
        if (input.mode === "sign-up") {
          const created = await client.signUpWithEmail({ email: input.email, password: input.password, name: input.name });
          if (created.token === null) return "verify";
        } else {
          await client.signInWithEmail({ email: input.email, password: input.password });
        }
      } catch (caught) {
        if (isEmailNotVerified(caught)) return "verify";
        setError(signInRefusalWords(caught));
        return "refused";
      } finally {
        setBusy(false);
      }
      await queryClient.invalidateQueries({ queryKey: ["identity"] });
      navigate("home");
      return "signed-in";
    },
    [client, navigate, queryClient],
  );

  /**
   * The verify page's button has confirmed the address (2026-10-22). A first
   * confirmation set the session cookie on that answer; asking who this is
   * now is the first signed-in request, on which a guest's work moves into the
   * account when this browser holds the guest cookie (apps/api identity.ts).
   * Signed in: home. Nobody: the address was confirmed before (better-auth
   * signs nobody in then), and the page says so.
   */
  const afterVerified = useCallback(async (): Promise<boolean> => {
    // Who this browser was before the press (the pre-release review, 2026-10-22). better-auth signs
    // a browser holding another account's session in to the link's account; everything cached for
    // the account it was (its cache key, engagement, profile, intake and reads) goes, as "Log out."
    // forgets it, and the new account's key is adopted from the server. A guest's is kept: its work
    // is claimed into the account, the same work.
    const before = queryClient.getQueryData<WhoAmI>(["identity"]);
    const next = await client.getIdentity();
    if (before?.kind === "user" && before.learnerId !== next.learnerId) {
      setStoredGuestId(null);
      setGuestId(null);
      forgetIdentity();
    }
    queryClient.setQueryData<WhoAmI>(["identity"], next);
    if (next.kind !== "user") return false;
    navigate("home");
    return true;
  }, [client, forgetIdentity, navigate, queryClient]);

  /**
   * A new password is saved (2026-10-22): the server has signed the account
   * out everywhere, this browser too (its sessions revoked, its claimed guest
   * cookies expired). Ask who this is; when it is nobody, forget everything
   * cached for the account, as logging out does.
   */
  const afterPasswordReset = useCallback(async (): Promise<void> => {
    const next = await client.getIdentity().catch((): WhoAmI => ({ kind: "none", learnerId: null, erasure: null, termsCurrent: true }));
    if (next.kind === "none") {
      setStoredGuestId(null);
      setGuestId(null);
      forgetIdentity();
    }
    queryClient.setQueryData<WhoAmI>(["identity"], next);
  }, [client, forgetIdentity, queryClient]);

  /** "accept the terms." on the terms paper: recorded with the server's version, then who this is again. */
  const acceptTerms = useCallback(async (): Promise<boolean> => {
    try {
      await client.acceptTerms();
    } catch {
      return false;
    }
    await queryClient.invalidateQueries({ queryKey: ["identity"] });
    return true;
  }, [client, queryClient]);

  const signInWithProvider = useCallback(
    async (provider: "google" | "facebook") => {
      // The provider's consent page is a full navigation, not a fetch: better-auth
      // hands back the URL and the browser goes there. It returns to callbackURL
      // with the session cookie already set.
      const url = await guardedCall(() =>
        client.socialSignInUrl(provider, `${globalThis.location.origin}/#/home`),
      );
      if (url === null) return;
      globalThis.location.href = url;
    },
    [client, guardedCall],
  );

  /** Takes the server's answer: what is still asked for, and the complete profile once there is one. The lines themselves are in the kept conversation. */
  const takeIntake = useCallback((extracted: ProfileExtracted): void => {
    setIntake(extracted);
    if (extracted.learnerProfileId !== null) setStoredProfileId(extracted.learnerProfileId);
    setProfile(extracted.profile);
  }, []);

  const extractProfile = useCallback(
    async (prose: string, intakeId: string | undefined): Promise<ProfileExtracted | null> => {
      const extracted = await guardedCall(() => client.extractProfile(prose, intakeId));
      if (extracted === null) return null;
      takeIntake(extracted);
      return extracted;
    },
    [client, guardedCall, takeIntake],
  );

  const confirmProfile = useCallback(async () => {
    const id = storedProfileId();
    if (id === null) return;
    await guardedCall(() => client.confirmProfile(id));
  }, [client, guardedCall]);

  /**
   * THE INTAKE AFTER A RELOAD (AJ intake, 2026-09-24): the conversation comes
   * back from the server like any other, and this asks what is still being
   * asked for. An intake the learner started over is not picked up again.
   */
  const intakeDismissed = useRef<string | null>(null);
  const profileIntake = useQuery({
    queryKey: ["profileIntake", guestId],
    enabled: guestId !== null && (route === "home" || route === "onboarding") && practiceRecord.data?.profile === null && intake === null,
    queryFn: () => client.getIntake(),
    retry: retryTransient,
    retryDelay,
  });
  useEffect(() => {
    const restored = profileIntake.data?.intake ?? null;
    if (restored === null || intake !== null || restored.intakeId === intakeDismissed.current) return;
    takeIntake(restored);
  }, [profileIntake.data, intake, takeIntake]);

  /**
   * Opening a case. One run per case: the server hands back a run already
   * open rather than starting another, and the learner walks straight back
   * in; `replaceOpen` — only ever sent after the "are you sure" — abandons
   * that run and begins again from the title page.
   *
   * Every door leads to the play screen now (2026-09-22): a fresh run opens
   * its book on the title page, with the prologue on the pages after it, and
   * a run under way opens on the story. The separate prologue screen remains
   * only for reading a case's prologue without entering it.
   */
  const startSession = useCallback(
    async (slug: string, replaceOpen = false) => {
      const created = await guardedCall(() => client.createSession(slug, { replaceOpen }));
      if (created === null) return;
      // The refusal line belongs to the run that earned it, never the next one.
      setDeclined(null);
      setChosenSlug(slug);
      setStoredSessionId(created.sessionId);
      setSessionId(created.sessionId);
      void queryClient.invalidateQueries({ queryKey: ["libraryState"] });
      navigate("play");
    },
    [client, guardedCall, navigate, queryClient],
  );

  const submitRecommendation = useCallback(
    async (text: string) => {
      if (sessionId === null) return;
      setDeclined(null);
      const result = await guardedCall(() => client.submitRecommendation(sessionId, text));
      if (result === null) return;
      setTelemetryToken((n) => n + 1);
      if (!result.ready) {
        setDeclined(result.message);
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ["session", sessionId] });
      navigate("play");
    },
    [client, guardedCall, sessionId, queryClient, navigate],
  );

  const toggleMechanics = useCallback(
    async (next: boolean) => {
      await guardedCall(() => client.patchSettings({ showMechanics: next }));
      await queryClient.invalidateQueries({ queryKey: ["settings", guestId] });
      // The record carries the mechanics line per turn when the setting is on; refetch it so
      // the desk changes as soon as the box is ticked (2026-09-15).
      await queryClient.invalidateQueries({ queryKey: ["session-turns"] });
    },
    [client, guardedCall, queryClient, guestId],
  );

  const toggleReadAloud = useCallback(
    async (next: boolean) => {
      await guardedCall(() => client.patchSettings({ readAloud: next }));
      await queryClient.invalidateQueries({ queryKey: ["settings", guestId] });
    },
    [client, guardedCall, queryClient, guestId],
  );

  /** CME stage 1: the two email choices and the recruiting fields, each saved as it changes. */
  const patchSettings = useCallback(
    async (patch: SettingsPatch) => {
      await guardedCall(() => client.patchSettings(patch));
      await queryClient.invalidateQueries({ queryKey: ["settings", guestId] });
    },
    [client, guardedCall, queryClient, guestId],
  );

  /**
   * The two voice functions the play surface receives (ADR 0011). Stable
   * identities, because the surface's read-aloud effect keys on them. Both
   * go through the typed client and nowhere else; a failure surfaces on the
   * control itself, in words, and never as a banner over the fiction.
   */
  const dictationAvailable = voiceCapabilities.data?.dictation ?? false;
  const narrationAvailable = voiceCapabilities.data?.narration ?? false;
  const dictate = useMemo(
    () => (dictationAvailable ? async (audio: Blob, durationSeconds: number): Promise<string> => (await client.transcribe(audio, { durationSeconds })).text : null),
    [client, dictationAvailable],
  );
  const narrationAudio = useMemo(
    () =>
      narrationAvailable && sessionId !== null
        ? (key: string): Promise<Blob> => client.narrationAudio(sessionId, Number.parseInt(key.replace(/^turn-/, ""), 10))
        : null,
    [client, narrationAvailable, sessionId],
  );

  const sendCoach = useCallback(
    async (text: string) => {
      setCoachPending(text);
      try {
        const reply = await guardedCall(() => client.sendCoachMessage(text));
        // The coach pushed a case: the hand changes, and the new card rises
        // from behind the panel on the next opening read.
        if (reply?.card !== null && reply?.card !== undefined) await queryClient.invalidateQueries({ queryKey: ["coachOpening"] });
        // The coach's reply may have come with an exercise code offered (Release C): the panel re-reads.
        if (reply?.practice !== null && reply?.practice !== undefined) await queryClient.invalidateQueries({ queryKey: ["practice"] });
        // Or with the focus changed by the learner's own words (Release D): chosen, or a use reported.
        if (reply?.focus !== null && reply?.focus !== undefined) await queryClient.invalidateQueries({ queryKey: ["focus"] });
      } finally {
        // The server kept both lines; read them back rather than trusting this page's copy.
        await queryClient.invalidateQueries({ queryKey: ["coachThread"] });
        setCoachPending(null);
      }
    },
    [client, guardedCall, queryClient],
  );

  /**
   * ONBOARDING THROUGH THE FRONT DOOR (pedagogy review, step 5). The learner
   * answers the coach's questions in the composer; the profile is inferred,
   * read back in the thread, and kept only when they say it is right. Then
   * the record, the shelf and the opening are read again: the coach now has
   * someone to speak to.
   */
  const onboardFromHome = useCallback(
    async (prose: string) => {
      setCoachPending(prose);
      try {
        await extractProfile(prose, intake?.intakeId);
      } finally {
        // The server kept both lines, the learner's and the coach's; read them back rather than trusting this page's copy.
        await queryClient.invalidateQueries({ queryKey: ["coachThread"] });
        setCoachPending(null);
      }
    },
    [extractProfile, intake, queryClient],
  );
  /** A level picked from the four offered: no extractor call, applied by code; the coach still answers. */
  const answerLevel = useCallback(
    async (level: ExperienceLevel) => {
      if (intake === null) return;
      const said = level.toLowerCase().replaceAll("_", " ");
      setCoachPending(said);
      try {
        const extracted = await guardedCall(() => client.answerProfile({ intakeId: intake.intakeId, experience_level: level }));
        if (extracted !== null) takeIntake(extracted);
      } finally {
        await queryClient.invalidateQueries({ queryKey: ["coachThread"] });
        setCoachPending(null);
      }
    },
    [client, guardedCall, intake, takeIntake, queryClient],
  );
  const confirmFromHome = useCallback(async () => {
    await confirmProfile();
    setIntake(null);
    setExchanges([]);
    // The opening is REMOVED, not invalidated: the coach now has someone to
    // speak to, and the page should say it is reading the record rather than
    // keep showing the pre-profile welcome while the new one is written.
    queryClient.removeQueries({ queryKey: ["coachOpening"] });
    // The coach said what it put on the desk (the papers line is in the thread), and the desk's papers arrive: the focus it laid there and the exercise.
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["coachThread"] }),
      queryClient.invalidateQueries({ queryKey: ["practiceRecord"] }),
      queryClient.invalidateQueries({ queryKey: ["libraryState"] }),
      queryClient.invalidateQueries({ queryKey: ["focus"] }),
      queryClient.invalidateQueries({ queryKey: ["practice"] }),
      // The arrival book exists from this moment (2026-10-11): the record's first row can ask for it.
      queryClient.invalidateQueries({ queryKey: ["arrival"] }),
    ]);
  }, [confirmProfile, queryClient]);
  const redoFromHome = useCallback(() => {
    intakeDismissed.current = intake?.intakeId ?? null;
    setIntake(null);
    setExchanges([]);
    setProfile(null);
  }, [intake]);
  /** THE REHEARSAL (idea 9): one move, one reflection, then the front door proper. */
  const rehearseFromHome = useCallback(
    async (text: string) => {
      const id = storedProfileId();
      if (id === null) return;
      setCoachPending(text);
      try {
        const reflection = await guardedCall(() => client.rehearse(id, text));
        if (reflection !== null) {
          setExchanges([{ from: "you", text }]);
          setRehearsal(reflection);
          // The move is now a page of the arrival book (2026-10-11): a book fetched
          // between the confirmation and this move is read again, not kept.
          await queryClient.invalidateQueries({ queryKey: ["arrival"] });
        }
      } finally {
        setCoachPending(null);
      }
    },
    [client, guardedCall, queryClient],
  );
  const finishRehearsal = useCallback(
    async (skip: boolean) => {
      const id = storedProfileId();
      if (skip && id !== null) await guardedCall(() => client.skipRehearsal(id));
      setRehearsal(null);
      setExchanges([]);
      // The opening has been on its way since the profile was confirmed (the
      // query refetched the moment confirmFromHome removed it), and nothing
      // the rehearsal does changes what it is written over. Keep that fetch:
      // removing it here started a second generation and made a new learner
      // wait the whole thing out again (production walkthrough, 2026-09-13).
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["practiceRecord"] }),
        // The rehearsal chapter is settled, made or declined (2026-10-11): a book
        // fetched before this moment says the learner had not reached it.
        queryClient.invalidateQueries({ queryKey: ["arrival"] }),
      ]);
    },
    [client, guardedCall, queryClient],
  );

  // WHERE THE FRONT DOOR IS IN ONBOARDING: the server's answer to "who is
  // this" decides. No confirmed profile: the intake. A confirmed profile that
  // has not yet made or declined its one rehearsal move: the rehearsal. Else
  // the coach proper.
  const recordProfile = practiceRecord.data?.profile;
  const onboarding: OnboardingState | null =
    guestId === null || recordProfile === undefined
      ? null
      : recordProfile === null
        ? { stage: "intake", exchanges: [], question: intake?.question ?? null, draft: intake?.profile ?? null, evidence: intake?.draft.evidence ?? [], rehearsal: null }
        : recordProfile.rehearsal_done
          ? null
          : { stage: "rehearsal", exchanges, question: null, draft: null, evidence: [], rehearsal };

  const showMechanics = settings.data?.showMechanics ?? false;
  const readAloud = settings.data?.readAloud ?? false;
  const blockedReason = budget.blocked
    ? `Session spend cap reached ($${budget.capUsd.toFixed(2)}). Raise it in the dev panel to keep going.`
    : null;

  const devPanel = DEV_PANEL_ENABLED ? (
    <DevPanel
      telemetry={telemetry}
      envelopeLog={session.envelopeLog}
      showMechanics={showMechanics}
      onToggleMechanics={(next) => void toggleMechanics(next)}
      seedFallback={null}
    />
  ) : null;

  const shellRoutes: readonly RouteName[] = ["home", "beat", "play", "recommend", "debrief", "audit", "profile", "coach", "library", "book", "skills"];

  /**
   * Is this render the Storyteller COMPOSITION, as opposed to one of the other
   * screens that live on the play route? The endgame (the cross-examination
   * and the closing scene) keeps the band it has always had; only the
   * Storyteller surface itself takes the whole viewport.
   */
  const storytellerPlay =
    route === "play" &&
    sessionId !== null &&
    session.data !== null &&
    session.data !== undefined &&
    session.data.status !== "ENDGAME_CROSS_EXAM" &&
    session.data.status !== "DEBRIEF" &&
    session.data.status !== "COMPLETE";

  const body = ((): ReactNode => {
    switch (route) {
      case "landing":
      case "scenarios":
        // This browser was someone before: wait for the server's word rather
        // than flash the landing page at a learner about to be sent home.
        if (route === "landing" && identity.isPending && guestId !== null) return null;
        return (
          <Landing
            startExpanded={route === "scenarios"}
            onNavigate={navigate}
            onOpenSettings={() => setSettingsOpen(true)}
          />
        );

      case "auth":
        return (
          <Auth
            // Remounted per tab, so arriving on "create an account." opens there.
            key={authMode}
            initialMode={authMode}
            onGuest={() => void startGuest()}
            onFreshGuest={() => void startGuest(true)}
            guestKnown={storedGuestId() !== null}
            // The check-your-email card's guest line: the server's word, as the verify page's (the pre-release review, 2026-10-22).
            guestWork={identity.data?.kind === "guest"}
            onGoogle={() => void signInWithProvider("google")}
            onEmail={signInWithEmail}
            // The account emails' two doors (2026-10-22); each says its own answer on the card.
            account={{
              resendVerification: (email) => client.sendVerificationEmail(email),
              requestPasswordReset: (email) => client.requestPasswordReset(email),
            }}
            busy={busy}
            error={error}
            // What the card holds survives a visit to the terms or the privacy page (the review,
            // 2026-10-22); its tab lives in the address, replaced rather than pushed, so "back." from
            // either page returns to it and the browser's Back does not step through tabs.
            draft={authDraft.current}
            onDraft={(draft) => {
              authDraft.current = draft;
            }}
            onModeChange={(mode) => {
              globalThis.history.replaceState(null, "", hashForAuth(mode));
              keepReturnHash();
            }}
          />
        );

      // The two reading pages (2026-10-22): "back." returns to the exact address the reading began
      // from (the review: `#/auth?mode=sign-up` keeps its tab, and the card what was typed); reached
      // cold, home for a signed-in learner, else sign in. Privacy says "back to sign in." when that
      // is where it goes, and "back." anywhere else.
      case "privacy":
        return <Privacy onBack={backFromReading} backWord={routeFromHash(readingReturn) === "auth" ? PRIVACY.backToSignIn : PRIVACY.back} />;

      case "terms":
        return <Terms onBack={backFromReading} />;

      // The two pages an account email leads to (2026-10-22): reached cold, outside the shell, the
      // hash's token handed only to the one request each makes, and only on a press.
      case "verify":
        return (
          <VerifyEmailPage
            key={mailToken ?? ""}
            client={client}
            token={mailToken}
            onVerified={afterVerified}
            onSignIn={() => openAuth("sign-in")}
            onLinkSettled={settleAccountLink}
            // The guest whose work the sign-in brings along, if this browser is one.
            guestKnown={identity.data?.kind === "guest"}
          />
        );

      case "reset":
        return (
          <ResetPasswordPage
            key={mailToken ?? ""}
            client={client}
            token={mailToken}
            onChanged={afterPasswordReset}
            onSignIn={() => openAuth("sign-in")}
            onAskAgain={() => openAuth("forgot")}
            onLinkSettled={settleAccountLink}
          />
        );

      // The two pages an email leads to (CME stage 3b, 2026-10-11): reached
      // cold like privacy, outside the shell, with the hash's token as the
      // whole claim; neither asks who this browser is.
      case "unsubscribe":
        return <UnsubscribePage client={client} token={mailToken} />;

      case "check-in":
        return <CheckInPage client={client} token={mailToken} />;

      case "home":
      case "onboarding":
        return (
          <HomeScreen
            opening={coachOpening.data ?? null}
            openingFailed={coachOpening.isError}
            firstVisit={(practiceRecord.data?.engagement_history.length ?? 0) === 0}
            // No identity at all gates the coach: there is nobody to read for.
            gated={guestId === null || identity.data?.kind === "none"}
            // An identity without a confirmed profile is onboarded here, by the
            // coach, in the thread (the server's answer to "who is this" decides).
            onboarding={onboarding}
            onOnboard={(prose) => void onboardFromHome(prose)}
            onAnswerLevel={(level) => void answerLevel(level)}
            onConfirmProfile={() => void confirmFromHome()}
            onRedoProfile={redoFromHome}
            onRehearse={(text) => void rehearseFromHome(text)}
            onSkipRehearsal={() => void finishRehearsal(true)}
            onBeginAfterRehearsal={() => void finishRehearsal(false)}
            thread={coachThread.data?.messages ?? []}
            pendingText={coachPending}
            busy={busy}
            error={error}
            cases={library.data ?? []}
            onSend={(text) => void sendCoach(text)}
            onViewMoment={viewMoment}
            {...deskPapers}
            onOpenSettings={() => setSettingsOpen(true)}
            dictate={dictate}
            libraryState={libraryState.data ?? null}
            onContinue={continueSession}
            onStart={(slug, replaceOpen) => void startSession(slug, replaceOpen)}
            onReadPrologue={(slug) => {
              setChosenSlug(slug);
              beat.reset();
              navigate("beat");
            }}
            onSetSaved={(slugs) => void setSavedCases(slugs)}
            onReadPlan={() => navigate("profile")}
            // The desk's arrange controls (2026-10-31): "tidy.", "arrange by."
            // and "undo." in the heading, in the piles form. They were staged
            // behind VITE_DESK_ARRANGE until Andre reviewed them on the bench
            // and the local desk and approved them for the live site the same
            // day; the bench alone can still show the first form, the rows.
            arrange="stacks"
          />
        );

      case "library":
        return (
          <CaseLibrary
            cases={library.data ?? []}
            state={libraryState.data ?? null}
            lagging={practiceRecord.data?.shape?.lagging ?? null}
            planDimensions={practiceRecord.data?.next_steps.map((step) => step.dimension) ?? []}
            busy={busy}
            onContinue={continueSession}
            onStart={(slug, replaceOpen) => void startSession(slug, replaceOpen)}
            onReadPrologue={(slug) => {
              setChosenSlug(slug);
              beat.reset();
              navigate("beat");
            }}
            onSetSaved={(slugs) => void setSavedCases(slugs)}
            onReadBook={openBook}
          />
        );

      case "book":
        // The record's first book (2026-10-11): how this learner met AJ. Its one door leads back to the record.
        if (bookArrival) return <ArrivalBookScreen onBack={() => navigate("profile")} />;
        if (bookSessionId === null) return <Nothing onStart={() => navigate("library")} />;
        return (
          <CasebookScreen
            key={bookSessionId}
            sessionId={bookSessionId}
            // "the tape.": this engagement's debrief, where the mechanics are disclosed in full.
            onTape={(id) => {
              setStoredSessionId(id);
              setSessionId(id);
              setFocusTurn(null);
              navigate("debrief");
            }}
            onTryAgain={(slug) => void startSession(slug)}
          />
        );

      case "beat":
        return opening.data === undefined ? (
          <Loading label="Opening the file." />
        ) : (
          <BeatPresentation
            opening={opening.data}
            index={beat.index}
            onNext={beat.next}
            resuming={session.data?.caseSlug === activeSlug && (session.turnCount > 0 || session.thread.some((entry) => entry.learnerText !== null))}
            onEnterPlay={() => {
              // The prologue being read may not be the open session's case
              // (a library card's "read the prologue again."): entering
              // from it opens THAT case — its own run if one is open, a
              // fresh one otherwise — never the other session.
              if (activeSlug !== null && session.data?.caseSlug !== activeSlug) void startSession(activeSlug, false);
              else navigate("play");
            }}
          />
        );

      case "play":
        if (sessionId === null) return <Nothing onStart={() => (activeSlug !== null ? void startSession(activeSlug) : navigate("library"))} />;
        if (session.data === null) return <Loading label="Reading the engagement." />;
        // The endgame lives on this route: once a recommendation has been
        // presented the partner's questions replace the desk, and once the
        // fourth is answered the engagement is over. Server status decides —
        // a reload lands on the same question.
        if (session.data.status === "ENDGAME_CROSS_EXAM" && session.data.crossExam !== null) {
          const exam = session.data.crossExam;
          return (
            <CrossExam
              question={exam.question?.text ?? ""}
              transcript={[
                { speaker: "you", text: exam.recommendationText },
                ...exam.exchanges.flatMap((x) => [
                  { speaker: "Doris", text: x.question },
                  { speaker: "you", text: x.answer },
                ]),
              ]}
              busy={session.busy}
              onAnswer={(text) => void session.submit(text)}
              onOpenSettings={() => setSettingsOpen(true)}
            />
          );
        }
        if (session.data.status === "DEBRIEF" || session.data.status === "COMPLETE") {
          // No recommendation on record means the days ran out (2026-09-17): show the scene that said so.
          return <EngagementOver onOpenDebrief={() => navigate("debrief")} closing={session.data.crossExam === null ? (session.thread.at(-1)?.narration ?? []) : []} />;
        }
        return (
          <PlaySurface
            sessionId={sessionId}
            view={session.data.view}
            // The scene is a region of the play composition now, not a band
            // above it (Storyteller layout, 2026-09-17). Same server view,
            // same turn plan, same section component — a different rectangle.
            environment={session.environment}
            environmentPlan={session.environmentPlan}
            // The book's first pages (2026-09-22): the case's title page and
            // its authored prologue, from the same opening the standalone
            // prologue screen reads. Strings only, by construction.
            prologue={
              opening.data === undefined || opening.data.slug !== session.data.caseSlug
                ? null
                : {
                    slug: opening.data.slug,
                    client: opening.data.client,
                    industry: opening.data.industry,
                    question: opening.data.engagementQuestion,
                    pages: opening.data.prologuePages.map((page) => ({
                      pageRef: String(page.index),
                      eyebrow: page.eyebrow,
                      title: page.title,
                      heroImageSlot: page.heroImageSlot,
                      blocks: page.blocks.map((block) => ({ kind: block.kind, text: block.text })),
                    })),
                  }
            }
            thread={session.thread}
            pendingText={session.pendingText}
            hypotheses={session.data.hypotheses}
            notes={session.data.notes}
            npcTells={session.decorations.npcTells}
            statuses={session.decorations.statuses}
            connection={session.connection}
            resumedFromTurn={session.resumedFromTurn}
            busy={session.busy}
            failure={session.failure}
            blockedReason={blockedReason}
            // Server state, not this page load: a learner who returns to a
            // session has taken turns the counter never saw.
            canRecommend={session.turnCount > 0 || session.thread.some((entry) => entry.learnerText !== null)}
            submit={(text) => void session.submit(text)}
            retry={() => void session.retry()}
            orientationNoteDocId={opening.data?.orientationNoteDocId ?? null}
            // The mentor's opening ask and the first-turn placeholders
            // (onboarding pass, 2026-09-13): strings, computed here so the play
            // surface never holds a turn number.
            mentorAsk={opening.data?.mentorName === undefined || opening.data.mentorName === null ? null : PLAY.mentorAsk.replace("{mentor}", opening.data.mentorName)}
            composerPlaceholder={PLAY.firstTurnPlaceholders[session.thread.filter((entry) => entry.learnerText !== null).length] ?? PLAY.inputPlaceholder}
            dictate={dictate}
            narrationAudio={narrationAudio}
            readAloud={readAloud}
            openSettings={() => setSettingsOpen(true)}
            openRecommend={() => navigate("recommend")}
          />
        );

      case "recommend":
        return (
          <RecommendationComposer
            caseName={opening.data?.client ?? "This engagement"}
            engagementQuestion={opening.data?.engagementQuestion ?? ""}
            busy={busy}
            declined={declined}
            onSubmit={(text) => void submitRecommendation(text)}
            onCancel={() => navigate("play")}
          />
        );

      case "debrief":
        return <Debrief debrief={debrief.data ?? null} audit={audit.data ?? null} onOpenAudit={() => navigate("audit")} focusTurn={focusTurn} />;

      case "audit":
        return <AuditView audit={audit.data ?? null} />;

      case "profile":
      case "coach":
        return (
          <PracticeRecordScreen
            profile={profile}
            record={practiceRecord.data ?? null}
            // The coach's reading of the whole record (2026-09-22): the same
            // opening the front door shows, written over the record by the
            // one prompt that already does this, and cached against it.
            summary={coachOpening.data?.welcome ?? null}
            summaryFailed={coachOpening.isError}
            coachMessages={coachThread.data?.messages.map((m) => ({ from: m.from, text: m.text, references: m.references })) ?? []}
            onViewMoment={viewMoment}
            coachBusy={busy}
            coachDisabled={!hasProfile}
            onSendCoach={(text) => void sendCoach(text)}
            onSubmitReflection={(answer) => void submitReflection(answer)}
            reflectionBusy={reflectionBusy}
            onOpenSettings={() => setSettingsOpen(true)}
            planAcknowledged={libraryState.data === undefined ? null : libraryState.data.planAcknowledgedAt !== null}
            onAcknowledgePlan={() => void acknowledgePlan()}
            onOpenSkills={() => navigate("skills")}
            onReadBook={openBook}
            onReadArrival={openArrivalBook}
          />
        );

      case "skills":
        return <SkillsScreen doctrine={skills.data ?? null} onBack={() => navigate("profile")} />;

      case "gallery":
        // Dev builds only; `DEV_PANEL_ENABLED` is a compile-time constant, so
        // in production this branch and the whole gallery module (and its
        // 40 embedded render plans) are tree-shaken away.
        // Also reachable in a static preview: with no backend to play against,
        // the 40-document gallery is the most honest thing a preview can show.
        return DEV_PANEL_ENABLED || IS_PREVIEW ? (
          <DocumentGallery />
        ) : (
          <Landing onNavigate={navigate} onOpenSettings={() => setSettingsOpen(true)} />
        );

      case "stage":
        // Dev builds only (ADR 0014): the narrative-to-scene compiler's
        // workbench. Tree-shaken away with the gallery in production.
        return DEV_PANEL_ENABLED || IS_PREVIEW ? (
          <NarrativeStage />
        ) : (
          <Landing onNavigate={navigate} onOpenSettings={() => setSettingsOpen(true)} />
        );

      case "play-review":
        // Dev builds only (Storyteller layout, 2026-09-17): the real play
        // surface against synthetic long content, for the size sweep. Tree-
        // shaken away with the gallery and the stage in production.
        return DEV_PANEL_ENABLED || IS_PREVIEW ? (
          <PlayReviewBench />
        ) : (
          <Landing onNavigate={navigate} onOpenSettings={() => setSettingsOpen(true)} />
        );

      case "front-door-review":
        // Dev builds only: the real front door against fixture openings, for
        // the same size sweep. Tree-shaken away in production.
        return DEV_PANEL_ENABLED || IS_PREVIEW ? (
          <FrontDoorReviewBench />
        ) : (
          <Landing onNavigate={navigate} onOpenSettings={() => setSettingsOpen(true)} />
        );

      default:
        return <Landing onNavigate={navigate} onOpenSettings={() => setSettingsOpen(true)} />;
    }
  })();

  const inShell = shellRoutes.includes(route);
  const guarded = <ScreenErrorBoundary resetKey={route}>{body}</ScreenErrorBoundary>;

  const settingsSheet = settingsOpen ? (
    <SettingsSheet
      showMechanics={showMechanics}
      readAloud={readAloud}
      narrationAvailable={narrationAvailable}
      busy={busy}
      onToggle={(next) => void toggleMechanics(next)}
      onToggleReadAloud={(next) => void toggleReadAloud(next)}
      onClose={() => setSettingsOpen(false)}
      emailChoices={
        emailChoices === null
          ? undefined
          : {
              state: emailChoices,
              saving: emailBusy,
              onSave: saveEmailChoices,
              onSignIn: () => {
                setSettingsOpen(false);
                openAuth("sign-in");
              },
            }
      }
      recruiting={
        guestId !== null && hasProfile
          ? { program: settings.data?.program ?? null, targetSeason: settings.data?.targetSeason ?? null, onChange: (patch) => void patchSettings(patch) }
          : undefined
      }
      onExportAccount={accountExportDoorOpen(guestId, signedIn) ? exportAccount : undefined}
      onExportCoachData={guestId !== null && coachDataControls ? exportCoachData : undefined}
      onDeleteCoachData={guestId !== null && coachDataControls ? deleteCoachData : undefined}
      accountErasure={
        erasureDoorOpen(identity.data?.kind)
          ? { emailVerified: erasureStatus.data?.emailVerified ?? null, onConfirm: requestErasure, onDownloadCoaching: downloadCoachingBeforeErasure, onDownloadAccount: downloadAccountBeforeErasure }
          : undefined
      }
    />
  ) : null;
  const erasureNotice = erasureRequested !== null ? <ErasureRequestedNotice requested={erasureRequested} onClose={() => setErasureRequested(null)} /> : null;

  // The region gate's screen, in place of everything else (every hook above has run), until the
  // learner follows one of its doors to a page they lead to (regionDoorFollowed).
  if (regionRefused) {
    const follow = (next: () => void) => () => {
      setRegionDoorFollowed(true);
      next();
    };
    const kind = identity.data?.kind;
    return (
      <>
        <RegionNotOffered
          doors={{
            kept: kind === "user" ? "account" : kind === "guest" ? "guest" : null,
            onSettings: () => setSettingsOpen(true),
            onLogOut: () => void logOut(),
            onSignIn: follow(() => openAuth("sign-in")),
            onPrivacy: follow(() => navigate("privacy")),
            onTerms: follow(() => navigate("terms")),
          }}
        />
        {settingsSheet}
        {erasureNotice}
      </>
    );
  }

  return (
    <>
      {inShell ? (
        <AppShell
          route={route}
          onNavigate={navigate}
          // Only an account can log out. A guest's cookie IS the guest: ending
          // it would leave their work unreachable, with nothing to sign back
          // in with.
          onLogOut={identity.data?.kind === "user" ? () => void logOut() : undefined}
          // A guest gets "Guest session." in its place: what a guest keeps, and
          // the two doors to keeping it (2026-09-24).
          onGuestAuth={identity.data?.kind === "guest" ? openAuth : undefined}
          band={route === "home" ? "home" : storytellerPlay || route === "book" ? "play" : route === "play" ? "grid" : "plain"}
          environment={session.environment}
          environmentPlan={session.environmentPlan}
        >
          {guarded}
        </AppShell>
      ) : (
        guarded
      )}

      {settingsSheet}

      {erasureNotice}

      {/* The terms paper (2026-10-22): at home only, never over play, for an account the server says has not accepted the current terms. */}
      {route === "home" && identity.data?.kind === "user" && !identity.data.termsCurrent && !termsPaperDown && erasureRequested === null ? (
        <TermsPaper onAccept={acceptTerms} onRead={() => navigate("terms")} onLater={() => setTermsPaperDown(true)} />
      ) : null}

      {devPanel}
      <PreviewNote />
    </>
  );
}

/** What the coach keeps, as a JSON file on the learner's disk (LAW 5). */
function saveCoachData(data: unknown): void {
  const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = COACH_DATA.exportFile;
  a.click();
  URL.revokeObjectURL(url);
}

/** Never a bare spinner: a label, and a route out if it stays. */
function Loading({ label }: { label: string }): ReactNode {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setSlow(true), 8_000);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="px-gutter pt-16 font-sans text-body text-ink-soft">
      <p className="m-0">{label}</p>
      {slow ? (
        <p className="mt-4 mb-0 text-small">
          This is taking longer than it should. The API may not be running — check `pnpm dev`, then reload.
        </p>
      ) : null}
    </div>
  );
}

function Nothing({ onStart }: { onStart: () => void }): ReactNode {
  return (
    <div className="px-gutter pt-16">
      <p className="m-0 font-sans text-body">No engagement open.</p>
      <button type="button" onClick={onStart} className="mt-4 cursor-pointer border-0 bg-transparent p-0 font-sans text-nav text-ink hover:bg-marker">
        open one.
      </button>
    </div>
  );
}
